import { useCallback, useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { isAdminUser } from "../../lib/userAccess";
import type { CallRecord, CallStatus, Sentiment, CallOutcome, TranscriptLine } from "./types";
import type { DateRangeValue } from "../../components/ui/date-range-picker";
import type { FilterState } from "../../components/ui/filter-popover";
import type { DataAvailability } from "../analytics/use-analytics";

export interface RetryContact {
  contact_id: string;
  name?: string;
  phone_number: string;
  metadata?: Record<string, unknown>;
  agent_id?: string;
  version_id?: string;
}

export interface RetryContactsResponse {
  contacts: RetryContact[];
  total: number;
  retry_context?: {
    agent_id?: string;
    agent_name?: string;
    version_id?: string;
    version_name?: string;
  } | null;
}

function mapSentiment(s: string | null): Sentiment {
  if (s === 'Positive' || s === 'positive') return 'positive';
  if (s === 'Negative' || s === 'negative') return 'negative';
  return 'neutral';
}

function mapStatus(s: string | null): CallStatus {
  if (s === 'completed' || s === 'success') return 'completed';
  return 'failed';
}

function transcriptSpeaker(value: unknown): TranscriptLine["speaker"] {
  const role = String(value || "").trim().toLowerCase();
  return role === "user" || role.includes("contact") || role.includes("customer")
    ? "contact"
    : "agent";
}

function transcriptTime(value: unknown): string {
  if (!value) return "";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function cleanTranscriptText(value: unknown): string {
  return String(value || "")
    .trim()
    .replace(/^\[\s*['"]?/, "")
    .replace(/['"]?\s*\]$/, "")
    .trim();
}

/** Matches the raw transcript parsing used by the legacy Recent Calls drawer. */
function parseRawTranscript(rawTranscript: string): TranscriptLine[] {
  const messages: TranscriptLine[] = [];
  let current: TranscriptLine | null = null;

  for (const line of rawTranscript.split("\n")) {
    const trimmed = line.trim();
    const match = trimmed.match(/^(assistant|user):\s*(.*)/i);

    if (match) {
      if (current?.text) messages.push(current);
      current = {
        speaker: transcriptSpeaker(match[1]),
        text: cleanTranscriptText(match[2]),
        at: "",
      };
    } else if (current && trimmed) {
      const continuation = cleanTranscriptText(trimmed);
      if (continuation) current.text += `${current.text ? "\n" : ""}${continuation}`;
    }
  }

  if (current?.text) messages.push(current);
  return messages;
}

function mapTranscript(call: any): TranscriptLine[] {
  const metadata = (
    call?.CONVERSATION_METADATA &&
    typeof call.CONVERSATION_METADATA === "object" &&
    !Array.isArray(call.CONVERSATION_METADATA)
  ) ? call.CONVERSATION_METADATA : {};

  const rawTranscript = call?.transcript ?? call?.llm_transcript ?? metadata.transcript;
  if (typeof rawTranscript === "string" && rawTranscript.trim()) {
    const parsed = parseRawTranscript(rawTranscript);
    if (parsed.length > 0) return parsed;
  }

  const transcriptMessages = call?.transcript_messages ?? call?.transcriptMessages;
  if (Array.isArray(transcriptMessages)) {
    const parsed = transcriptMessages
      .map((message: any) => ({
        speaker: transcriptSpeaker(message.role ?? message.speaker),
        text: cleanTranscriptText(message.text ?? message.content),
        at: transcriptTime(message.created_at ?? message.timestamp),
        // Present only on a turn the caller cut off. Left undefined otherwise so
        // the view can tell "played in full" from "cut before any audio".
        spokenPrefix: spokenPrefixOf(message),
      }))
      .filter((message: TranscriptLine) => Boolean(message.text) || message.spokenPrefix !== undefined);
    if (parsed.length > 0) return parsed;
  }


  if (Array.isArray(rawTranscript)) {
    return rawTranscript
      .map((message: any) => ({
        speaker: transcriptSpeaker(message.speaker ?? message.role),
        text: cleanTranscriptText(message.text ?? message.content),
        at: transcriptTime(message.timestamp ?? message.created_at),
        spokenPrefix: spokenPrefixOf(message),
      }))
      .filter((message: TranscriptLine) => Boolean(message.text) || message.spokenPrefix !== undefined);
  }

  return [];
}

/**
 * The measured part of a turn that reached the speaker, or undefined.
 *
 * Gated on ``playback`` because that is what records the cut, and the server
 * already applies the same rule. An empty string is preserved: it means the
 * caller cut the agent off before any audio was voiced, which is a different
 * thing from having no measurement at all.
 */
function spokenPrefixOf(message: any): string | undefined {
  const playback = message?.playback;
  if (typeof playback !== "string" || playback === "complete") return undefined;
  const prefix = message.spoken_prefix ?? message.spokenPrefix;
  return typeof prefix === "string" ? cleanTranscriptText(prefix) : undefined;
}

export function useCalls(dateRange: DateRangeValue, filters: FilterState = {}) {
  const { token, user } = useAuth();
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [dataAvailability, setDataAvailability] = useState<DataAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);


  const fetchCalls = useCallback(async (currentCursor: string | null = null) => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const isAdmin = isAdminUser(user);
      const endpoint = isAdmin
        ? `${apiUrl}/api/v1/admin/master-extract`
        : `${apiUrl}/api/v1/analytics`;

      const formatDate = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };

      const params: any = { 
        limit: 50,
      };
      
      // Overrule the date filter if a search query is present (search across all time)
      if (!filters.search) {
        params.start_date = formatDate(dateRange.start);
        params.end_date = formatDate(dateRange.end);
      }
      if (currentCursor) params.cursor = currentCursor;

      if (filters.search) params.search = filters.search;
      if (filters.search_field && filters.search_field !== 'all') params.search_field = filters.search_field;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.direction && filters.direction !== 'all') params.direction = filters.direction;
      if (filters.campaign) params.campaign = filters.campaign;
      if (filters.voice) params.voice = filters.voice;
      if (filters.max_duration) {
        params.max_duration = filters.max_duration;
        params.duration_operator = filters.duration_operator || ">=";
      }
      if (filters.agent_name) params.agent_name = filters.agent_name; // Pass agent display name for analytics endpoint
      if (filters.version_name) params.version_name = filters.version_name;
      if (filters.workflow_id) params.workflow_id = filters.workflow_id;
      if (filters.direction && filters.direction !== 'all') params.direction = filters.direction;
      // dial_workflow_id/dial_agent_name are the dedicated Workflow/Dialed By
      // filters (distinct from the combined Agent dropdown's agent_id/workflow_id
      // pair above) — the backend's workflow_id/agent_name params double as both.
      if (filters.dial_workflow_id) params.workflow_id = filters.dial_workflow_id;
      if (filters.dial_agent_name) params.agent_name = filters.dial_agent_name;
      if (filters.sent_channel) params.sent_channel = filters.sent_channel;



      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      console.log("Analytics Response:", res.data);
      let rawCalls: any[] = [];
      if (isAdmin && res.data?.data) {
        rawCalls = res.data.data.flatMap((client: any) => client.calls || []);
      } else if (res.data?.recent_calls) {
        rawCalls = res.data.recent_calls;
      }
      console.log("Extracted rawCalls length:", rawCalls?.length);

      const mapped: CallRecord[] = [];
      for (const c of rawCalls || []) {
        try {
          let parsedCallOutcomes = c.call_outcomes || {};
          if (typeof parsedCallOutcomes === 'string') {
            try { parsedCallOutcomes = JSON.parse(parsedCallOutcomes); } catch { parsedCallOutcomes = {}; }
          }
          let parsedOutcome = c.outcome || {};
          if (typeof parsedOutcome === 'string') {
            try { parsedOutcome = JSON.parse(parsedOutcome); } catch { parsedOutcome = {}; }
          }

          const rawOutcomes = {
            ...(typeof parsedCallOutcomes === 'object' && parsedCallOutcomes !== null ? parsedCallOutcomes : {}),
            ...(typeof parsedOutcome === 'object' && parsedOutcome !== null ? parsedOutcome : {})
          };

          const outcomes: CallOutcome[] = Object.entries(rawOutcomes).map(([k, v]) => ({
            field: k,
            value: String(v)
          }));

          const transcript = mapTranscript(c);

          let aiSummary: string[] = [];
          if (Array.isArray(c.summary)) {
            aiSummary = c.summary;
          } else if (typeof c.summary === 'string') {
            aiSummary = c.summary.split('\n').filter(Boolean);
          }

          const rawSchedId = String(c.schedule_id || c.scheduleId || '').trim();
          const rawExecId = String(c.id || c.execution_id || '').trim();
          const rawContactId = String(c.contact_id || c.contactId || '').trim();

          const isTestCall = Boolean(
            c.is_test_call ||
            c.is_test ||
            rawSchedId.toLowerCase().startsWith('test-') ||
            rawSchedId.toLowerCase().startsWith('test_') ||
            rawExecId.toLowerCase().startsWith('test-') ||
            rawExecId.toLowerCase().startsWith('test_') ||
            rawContactId.toLowerCase().startsWith('test-') ||
            rawContactId.toLowerCase().startsWith('test_')
          );

          // A scheduled call has a valid scheduleId and is NOT a test call
          const isSchedPrefix = rawSchedId.toLowerCase().startsWith('sched_') || rawSchedId.toLowerCase().startsWith('sched-');
          const isScheduledCall = !isTestCall && Boolean(
            rawSchedId &&
            rawSchedId.toUpperCase() !== 'NA' &&
            (isSchedPrefix || !rawSchedId.toLowerCase().startsWith('test'))
          );

          // ONLY scheduled calls with a valid contactId can be retried
          const hasValidContact = Boolean(
            rawContactId &&
            rawContactId.toUpperCase() !== 'NA' &&
            !rawContactId.toLowerCase().startsWith('test')
          );
          const retryable = isScheduledCall && hasValidContact;
          const rawDirection = String(c.direction || (c.CONVERSATION_METADATA || {}).direction || 'outbound').toLowerCase();
          const direction: 'inbound' | 'outbound' = rawDirection === 'inbound' ? 'inbound' : 'outbound';
          const destinationDid = c.destination_did || (c.CONVERSATION_METADATA || {}).destination_did || (c.CONVERSATION_METADATA || {}).did_number || c.did_number || undefined;

          mapped.push({
            executionId: rawExecId || Math.random().toString(),
            contactId: rawContactId,
            phone: c.customer_phone || c.customerPhone || c.user_number || c.userNumber || '',
            direction,
            destinationDid,
            scheduleId: rawSchedId,
            agent: c.agent_name || c.agentName || 'Unknown Agent',
            agentId: c.agent_id || '',
            workflowId: c.multi_agent_config_id || '',
            version: c.version_name || c.versionName || 'v1',
            voice: c.voice || (c.CONVERSATION_METADATA || {}).voice || 'Default Voice',
            durationSec: parseFloat(c.duration_seconds || c['duration (s)'] || c.duration_s || 0),
            createdAt: c.created_at || c.timestamp || new Date().toISOString(),
            status: mapStatus(c.status),
            hungBy: String(c.hangup_by || c.hangupBy || c.hung_by || 'system').toLowerCase() as any,
            campaign: c.campaign || (c.CONVERSATION_METADATA || {}).campaign || 'NA',
            sentiment: mapSentiment(c.sentiment),
            retryable,
            isTestCall,
            isScheduledCall,
            outcomes,
            transcript,
            aiSummary,
            recordingUrl: c.recording_url,
            callId: String(c.call_id || rawExecId || '').trim() || undefined,
          });
        } catch (err) {
          console.error("Failed to map individual call:", c, err);
        }
      }
      
      console.log("Successfully mapped calls:", mapped.length);

      setCalls(prev => currentCursor ? [...prev, ...mapped] : mapped);
      setCursor(res.data?.next_cursor || null);
      setHasMore(!!res.data?.next_cursor);
      if (!currentCursor) {
        setDataAvailability(res.data?.data_availability || res.data?.dataAvailability || null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [
    token, user, dateRange.start, dateRange.end,
    filters.search, filters.search_field, filters.status, filters.direction, filters.campaign,
    filters.voice, filters.max_duration, filters.duration_operator, filters.agent_name, filters.version_name, filters.workflow_id,
    filters.dial_workflow_id, filters.dial_agent_name, filters.sent_channel
  ]);

  useEffect(() => {
    if (token) {
      fetchCalls();
    }
  }, [token, fetchCalls]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore && cursor) {
      fetchCalls(cursor);
    }
  }, [loading, hasMore, cursor, fetchCalls]);

  const seedDemoData = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${apiUrl}/api/v1/seed-demo-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCalls();
    } catch (e) {
      console.error(e);
    }
  }, [token, fetchCalls]);

  const queueRetry = useCallback(async (contactIds: string[]) => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    const response = await axios.get<RetryContactsResponse>(
      `${apiUrl}/api/v1/schedules/retry-contacts`,
      {
        params: { contact_ids: contactIds.join(",") },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  }, [token]);

  return { calls, dataAvailability, loading: initialLoad && loading, hasMore, loadMore, seedDemoData, queueRetry };
}
